import { CombinedGraphQLErrors } from '@apollo/client'
import { useMutation, useQuery } from '@apollo/client/react'
import { Button, Flex, Input, notification } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ContainerDocumentDocument,
  type ContainerDocumentQuery,
  DocumentsInOpenReturnDocument,
  RequestReturnDocument,
} from '~app/graph/generated/graphql'
import SectionCard from './SectionCard'
import { useCustomerDocumentAction } from './useCustomerAccountActions'

enum DocumentActionButtonLabelOptions {
  addToReturnBasket = 'addToReturnBasket',
  unShred = 'unShred',
  unDelete = 'unDelete',
  noAvailableActions = 'noAvailableActions',
  waitingForInput = 'waitingForInput',
}

const getDocumentActionButtonLabel = (
  containerDocumentData: Pick<
    ContainerDocumentQuery['containerDocument'],
    'isDestroyed' | 'currentLocationType' | 'canReturn'
  >
) => {
  return containerDocumentData.isDestroyed
    ? DocumentActionButtonLabelOptions.unDelete
    : containerDocumentData.currentLocationType === 'ShredBasket'
      ? DocumentActionButtonLabelOptions.unShred
      : containerDocumentData.canReturn
        ? DocumentActionButtonLabelOptions.addToReturnBasket
        : DocumentActionButtonLabelOptions.noAvailableActions
}

/*
 * Component
 */

interface Props {
  customerId: string
}

const ActionsSection = ({ customerId }: Props) => {
  const { t } = useTranslation()
  const [containerIdFieldValue, setContainerIdFieldValue] = useState<
    string | undefined
  >(undefined)
  const [documentInputError, setDocumentErrorInput] = useState<string | null>(
    null
  )

  const {
    data: documentsInReturnBasket,
    loading: documentsInReturnBasketLoading,
    refetch: refetchDocumentsInReturnBasket,
  } = useQuery(DocumentsInOpenReturnDocument, {
    variables: { customerId },
    fetchPolicy: 'network-only',
  })

  const {
    loading: containerDocumentLoading,
    data: containerDocumentData,
    refetch: containerDocumentRefetch,
  } = useQuery(ContainerDocumentDocument, {
    skip: !containerIdFieldValue,
    variables: {
      containerId: containerIdFieldValue ?? '',
    },
  })

  useEffect(() => {
    if (containerIdFieldValue) {
      containerDocumentRefetch({
        containerId: containerIdFieldValue,
      })
        .then(() => {
          setDocumentErrorInput(null)
        })
        .catch(error => setDocumentErrorInput(error.message))
    }
  }, [containerIdFieldValue])

  const [requestReturn, { loading: requestReturnLoading }] = useMutation(
    RequestReturnDocument
  )
  const { onCustomerAccountAction } = useCustomerDocumentAction(
    containerDocumentData && containerDocumentData.containerDocument
  )

  const orderReturnsBasketButtonDisabled =
    requestReturnLoading ||
    documentsInReturnBasketLoading ||
    (documentsInReturnBasket &&
      documentsInReturnBasket.documentsInOpenReturn.length === 0)

  const orderReturnsBasket = async () => {
    try {
      await requestReturn({ variables: { customerId } })
      refetchDocumentsInReturnBasket()
      notification.success({
        message: t(
          'customerDocumentActions.successMessages.returnSentSuccessfully'
        ),
      })
    } catch (error) {
      // Apollo Client 4 rejects with CombinedGraphQLErrors, which carries the
      // list as `errors`
      const { code, startedAt } = CombinedGraphQLErrors.is(error)
        ? (error.errors[0]?.extensions ?? {})
        : {}
      const startedAtLabel =
        typeof startedAt === 'string'
          ? dayjs(startedAt).format('DD.MM.YYYY HH:mm')
          : undefined
      notification.error({
        placement: 'bottomRight',
        message: t('misc.error'),
        // the _startedAt variant is only tried once the timestamp is
        // formatted, so its placeholder never renders unfilled
        description: t(
          [
            ...(startedAtLabel
              ? [`customerDocumentActions.errorMessages.${code}_startedAt`]
              : []),
            `customerDocumentActions.errorMessages.${code}`,
            'customerDocumentActions.errorMessages.generic',
          ],
          { startedAt: startedAtLabel }
        ),
      })
    }
  }

  return (
    <SectionCard
      rows={[
        {
          content: (
            <Flex align="center" justify="space-between">
              <span>
                {!documentsInReturnBasketLoading && documentsInReturnBasket
                  ? documentsInReturnBasket.documentsInOpenReturn.length
                  : '...'}
              </span>
              <Button
                type="default"
                disabled={orderReturnsBasketButtonDisabled}
                loading={requestReturnLoading}
                onClick={orderReturnsBasket}
              >
                {t('misc.orderReturns')}
              </Button>
            </Flex>
          ),
          label: t('misc.returnBasket'),
        },
        {
          content: (
            <Flex gap={8} align="start">
              <div style={{ position: 'relative', flex: 1 }}>
                <Input
                  placeholder={t(
                    'customerDetails.fields.containerIdPlaceholder'
                  )}
                  onPressEnter={event => {
                    setContainerIdFieldValue(event.currentTarget.value)
                  }}
                />
                {documentInputError && (
                  <span style={{ color: 'red', fontSize: 12 }}>
                    {documentInputError}
                  </span>
                )}
              </div>
              {containerDocumentData?.containerDocument ? (
                <Button
                  disabled={
                    getDocumentActionButtonLabel(
                      containerDocumentData.containerDocument
                    ) === DocumentActionButtonLabelOptions.noAvailableActions ||
                    containerDocumentLoading ||
                    !!documentInputError
                  }
                  type="primary"
                  onClick={() =>
                    onCustomerAccountAction()
                      .then(() => {
                        refetchDocumentsInReturnBasket()
                        notification.success({
                          message: t(
                            `customerDocumentActions.successMessages.${getDocumentActionButtonLabel(
                              containerDocumentData.containerDocument
                            )}`
                          ),
                        })
                      })
                      .catch(e => {
                        notification.error({
                          message: e.message,
                        })
                      })
                  }
                >
                  {t(
                    `customerDocumentActions.${getDocumentActionButtonLabel(
                      containerDocumentData.containerDocument
                    )}`
                  )}
                  {getDocumentActionButtonLabel(
                    containerDocumentData.containerDocument
                  ) === DocumentActionButtonLabelOptions.unShred &&
                    ' - ' +
                      dayjs(
                        containerDocumentData.containerDocument.createdAt
                      ).format('DD.MM.YYYY')}
                </Button>
              ) : (
                <Button disabled>
                  {t(
                    `customerDocumentActions.${DocumentActionButtonLabelOptions.noAvailableActions}`
                  )}
                </Button>
              )}
            </Flex>
          ),
          label: t('customerDetails.fields.document'),
        },
      ]}
      title={t('customerDetails.sections.actions')}
    />
  )
}

export default ActionsSection
